// Floating settings popover for the colorbar. Opened by the palette icon
// (#colorbar-settings-btn) on the collapsed colorbar; quick access to the
// colormap picker, Min/Max sliders, and the log / mask-bounds / units toggles.
// DOM-only — no THREE.

import { store, params as paramsProxy } from '@store/instance';
import { INTENTS } from '@store/intents';
import { calculateColormapBoundsFromArray } from './colorbar';
import { sampleColormap as sampleColormapPure } from '@physics/colormap';
import { colormapOptions } from './panel/options';
import { createRangeControl } from './controls/rangeControl';
import { rangeFormat } from './panel/numberFormat';
import type { RangeControlHandle } from './controls/rangeControl';
import type { ViewerContext } from '@app/context';
import type { ParamsState } from '@store/schema';

interface PopoverEls {
    root: HTMLDivElement;
    swatch: HTMLCanvasElement;
    swatchLabel: HTMLSpanElement;
    transparentBox: HTMLInputElement;
    logBox: HTMLInputElement;
    unitsSeg: HTMLDivElement;
    minMaxHost: HTMLDivElement;
}

function currentFieldArray(viewer: ViewerContext): Float32Array | null | undefined {
    return (viewer.field.currentComponent === 'magnitude')
        ? viewer.field.gridFlat
        : (viewer.field.data ? viewer.field.data[viewer.field.currentComponent] : null);
}

function paintSwatch(canvas: HTMLCanvasElement, viewer: ViewerContext, cmapName: string): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width, h = canvas.height;
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    const steps = 48;
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const c = sampleColormapPure(viewer.colormap.defs, cmapName, t);
        grad.addColorStop(t, `rgb(${c.r},${c.g},${c.b})`);
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
}

function computeSliderBounds(viewer: ViewerContext, params: ParamsState): { sliderMin: number; sliderMax: number } {
    const data = currentFieldArray(viewer);
    if (!data || !data.length) {
        // Conservative fallback: stretch around the current min/max.
        const span = Math.max(1e-6, params.colormapMax - params.colormapMin);
        return { sliderMin: params.colormapMin - span, sliderMax: params.colormapMax + span };
    }
    const stats = calculateColormapBoundsFromArray(data, { centerOnZero: params.centerOnZero });
    const { minBound, maxBound, stdDev, absoluteMin, absoluteMax, hasBothSigns } = stats;
    const twoSigma = 2 * stdDev;
    if (hasBothSigns) return { sliderMin: -twoSigma, sliderMax: twoSigma };
    return {
        sliderMin: Math.max(absoluteMin, minBound - stdDev),
        sliderMax: Math.min(absoluteMax, maxBound + stdDev),
    };
}

function buildPopover(viewer: ViewerContext): PopoverEls {
    const root = document.createElement('div');
    root.id = 'colorbar-settings-popover';
    root.className = 'colorbar-popover';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Colormap settings');

    // Row 1: swatch + name (clickable → opens cmap picker)
    const swatchRow = document.createElement('button');
    swatchRow.type = 'button';
    swatchRow.className = 'cmap-swatch-row';
    swatchRow.setAttribute('aria-label', 'Choose colormap');
    const swatch = document.createElement('canvas');
    swatch.className = 'cmap-swatch';
    swatch.width = 96; swatch.height = 16;
    const swatchLabel = document.createElement('span');
    swatchLabel.className = 'cmap-swatch-label';
    swatchRow.appendChild(swatch);
    swatchRow.appendChild(swatchLabel);

    // Row 2: two compact checkboxes side-by-side. Wrapped in a group so
    // hover-brighten can be scoped per-label instead of lighting up both
    // toggles when you mouse over either.
    const checkboxGroup = document.createElement('div');
    checkboxGroup.className = 'cmap-checkbox-group';

    const transparentRow = document.createElement('label');
    transparentRow.className = 'cmap-checkbox-item';
    const transparentBox = document.createElement('input');
    transparentBox.type = 'checkbox';
    const transparentText = document.createElement('span');
    transparentText.textContent = 'Mask bounds';
    transparentRow.appendChild(transparentBox);
    transparentRow.appendChild(transparentText);

    const logRow = document.createElement('label');
    logRow.className = 'cmap-checkbox-item';
    const logBox = document.createElement('input');
    logBox.type = 'checkbox';
    const logText = document.createElement('span');
    logText.textContent = 'Log';
    logRow.appendChild(logBox);
    logRow.appendChild(logText);

    checkboxGroup.appendChild(transparentRow);
    checkboxGroup.appendChild(logRow);

    // Units mode (stub). A segmented control rather than a checkbox: it's a
    // choice between mutually-exclusive named modes, not a boolean, and a
    // future 'SI' segment drops in here. Flips the [native]/[code] label
    // prefix; conversion math lands with normalization metadata.
    const unitsRow = document.createElement('div');
    unitsRow.className = 'cmap-units-row';
    unitsRow.title = 'Colorbar units (stub — SI conversion lands with normalization metadata)';
    const unitsLabel = document.createElement('span');
    unitsLabel.className = 'cmap-units-label';
    unitsLabel.textContent = 'Units';
    const unitsSeg = document.createElement('div');
    unitsSeg.className = 'cmap-seg';
    unitsSeg.setAttribute('role', 'group');
    unitsSeg.setAttribute('aria-label', 'Colorbar units');
    for (const mode of ['native', 'code'] as const) {
        const opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'cmap-seg-opt';
        opt.dataset.mode = mode;
        opt.textContent = mode;
        unitsSeg.appendChild(opt);
    }
    unitsRow.appendChild(unitsLabel);
    unitsRow.appendChild(unitsSeg);

    // Min/Max host — two RangeControls (drag-to-scrub + click-to-edit text)
    // mounted directly here on open.
    const minMaxHost = document.createElement('div');
    minMaxHost.className = 'cmap-controls-host';

    // Decorative tail that points at the gear button. The arrow's
    // direction (and which edge of the popover it sits on) is driven by
    // `data-edge` written in `positionPopover`; `--arrow-pos` is the
    // pixel offset along that edge so it stays aligned with the gear
    // even when viewport clamping shifts the popover.
    const arrow = document.createElement('div');
    arrow.className = 'colorbar-popover-arrow';
    arrow.setAttribute('aria-hidden', 'true');

    root.appendChild(arrow);
    root.appendChild(swatchRow);
    root.appendChild(minMaxHost);
    root.appendChild(checkboxGroup);
    root.appendChild(unitsRow);
    document.body.appendChild(root);

    // Initial swatch paint so callers can rely on it being primed.
    paintSwatch(swatch, viewer, paramsProxy.colormap);

    // Stash the swatch row so the picker can anchor to it.
    (root as HTMLDivElement & { __swatchRow: HTMLButtonElement }).__swatchRow = swatchRow;

    return { root, swatch, swatchLabel, transparentBox, logBox, unitsSeg, minMaxHost };
}

function positionPopover(root: HTMLElement, anchor: HTMLElement, edge: string): void {
    // Anchor to the gear button's rect so the popover pops up next to
    // where the user clicked. `edge` is read from the colorbar pane (not
    // the button) and decides which side of the button to extend toward.
    const rect = anchor.getBoundingClientRect();
    // Gap = arrow tail length (~10px) + breathing room between arrow tip
    // and gear icon so the tail doesn't crowd the gear.
    const gap = 16;
    // Measure popover after it's been added; if hidden, use offsetWidth/Height
    // hint via temporary visibility-hidden display.
    const pr = root.getBoundingClientRect();
    const pw = pr.width || 220;
    const ph = pr.height || 180;
    let top = 0, left = 0;
    if (edge === 'right') {
        // Colorbar on right → popover to the LEFT of the gear, aligned
        // with the gear's vertical center.
        left = rect.left - pw - gap;
        top = rect.top + rect.height / 2 - ph / 2;
    } else if (edge === 'left') {
        left = rect.right + gap;
        top = rect.top + rect.height / 2 - ph / 2;
    } else if (edge === 'top') {
        // Colorbar on top → popover BELOW the gear, horizontally centered
        // on the gear.
        left = rect.left + rect.width / 2 - pw / 2;
        top = rect.bottom + gap;
    } else { // bottom
        left = rect.left + rect.width / 2 - pw / 2;
        top = rect.top - ph - gap;
    }
    // Keep inside viewport.
    const margin = 8;
    left = Math.max(margin, Math.min(window.innerWidth - pw - margin, left));
    top = Math.max(margin, Math.min(window.innerHeight - ph - margin, top));
    root.style.left = `${left}px`;
    root.style.top = `${top}px`;

    // Arrow direction comes from the colorbar's `data-edge`; the arrow
    // offset is the gear's center mapped into the popover's local
    // coordinates so it stays pointed at the gear after viewport
    // clamping shifts the popover off-center.
    root.dataset.edge = edge;
    const gearCenterX = rect.left + rect.width / 2;
    const gearCenterY = rect.top + rect.height / 2;
    let arrowPos: number;
    // 16px keeps the 10px tail clear of the popover's 10px border radius.
    if (edge === 'right' || edge === 'left') {
        arrowPos = Math.max(16, Math.min(ph - 16, gearCenterY - top));
    } else {
        arrowPos = Math.max(16, Math.min(pw - 16, gearCenterX - left));
    }
    root.style.setProperty('--arrow-pos', `${arrowPos}px`);
}

function buildCmapPicker(viewer: ViewerContext, onPick: (name: string) => void): HTMLDivElement {
    const root = document.createElement('div');
    root.id = 'cmap-picker-popover';
    // `.open` flips the shared `.colorbar-popover { display: none }` rule —
    // without it the picker is built but never paints.
    root.className = 'colorbar-popover cmap-picker open';
    root.setAttribute('role', 'listbox');

    const opts = colormapOptions(viewer);
    const current = paramsProxy.colormap;
    Object.keys(opts).forEach((name) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'cmap-pick-row' + (name === current ? ' selected' : '');
        row.setAttribute('role', 'option');
        const sw = document.createElement('canvas');
        sw.className = 'cmap-swatch';
        sw.width = 28; sw.height = 16;
        paintSwatch(sw, viewer, name);
        const lbl = document.createElement('span');
        lbl.className = 'cmap-pick-label';
        lbl.textContent = name;
        row.appendChild(sw);
        row.appendChild(lbl);
        row.addEventListener('click', (e) => { e.stopPropagation(); onPick(name); });
        root.appendChild(row);
    });

    document.body.appendChild(root);
    return root;
}

function positionPicker(picker: HTMLElement, anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    const pr = picker.getBoundingClientRect();
    const pw = pr.width || 200;
    const ph = pr.height || 260;
    const gap = 6;
    let left = rect.right + gap;
    let top = rect.top;
    // If overflow on the right, place to the left of the swatch row.
    const margin = 8;
    if (left + pw + margin > window.innerWidth) {
        left = rect.left - pw - gap;
    }
    left = Math.max(margin, Math.min(window.innerWidth - pw - margin, left));
    top = Math.max(margin, Math.min(window.innerHeight - ph - margin, top));
    picker.style.left = `${left}px`;
    picker.style.top = `${top}px`;
}

export function mountColorbarSettings(viewer: ViewerContext): () => void {
    const btn = document.getElementById('colorbar-settings-btn') as HTMLButtonElement | null;
    const colorbar = document.getElementById('colorbar-container') as HTMLDivElement | null;
    if (!btn || !colorbar) return () => { /* nothing to unmount */ };

    let els: PopoverEls | null = null;
    let picker: HTMLDivElement | null = null;
    let isOpen = false;
    // Cached slider extents. `computeSliderBounds` walks the entire field
    // array (mean + stdDev + median sort) — on a 256^3 grid that's ~16M
    // ops, so we recompute only when the popover opens or the field
    // identity changes, not on every params write.
    let cachedExtents: { sliderMin: number; sliderMax: number } | null = null;
    let cachedExtentsKey = '';
    // Two RangeControls (Min/Max) mounted directly into the popover. Rebuilt
    // each open so a newly-loaded dataset gets fresh extents (the control locks
    // `min`/`max` at construction); disposed in disposePane.
    let rangeCtrls: { min: RangeControlHandle | null; max: RangeControlHandle | null } = { min: null, max: null };

    const fieldKey = (): string => `${paramsProxy.dataset}::${viewer.field.currentComponent}`;

    const ensureExtents = (force = false): { sliderMin: number; sliderMax: number } => {
        const key = fieldKey();
        if (!force && cachedExtents && cachedExtentsKey === key) return cachedExtents;
        cachedExtents = computeSliderBounds(viewer, paramsProxy);
        cachedExtentsKey = key;
        return cachedExtents;
    };

    const disposePane = (): void => {
        rangeCtrls.min?.dispose();
        rangeCtrls.max?.dispose();
        rangeCtrls = { min: null, max: null };
        if (els) els.minMaxHost.innerHTML = '';
    };

    const buildSliders = (): void => {
        if (!els) return;
        disposePane();
        const { sliderMin, sliderMax } = ensureExtents();
        const step = Math.max(1e-6, (sliderMax - sliderMin) / 1000);
        const fmt = rangeFormat(sliderMin, sliderMax);
        const host = els.minMaxHost;
        const mkRow = (key: 'min' | 'max', label: string): RangeControlHandle => {
            const row = document.createElement('div');
            row.className = 'cmap-minmax-row';
            const lbl = document.createElement('span');
            lbl.className = 'cmap-minmax-label';
            lbl.textContent = label;
            const cell = document.createElement('div');
            cell.className = 'cmap-minmax-value';
            row.append(lbl, cell);
            host.appendChild(row);
            const send = (v: number): void => {
                if (!isFinite(v)) return;
                store.dispatch(INTENTS.SET_COLORMAP_RANGE, key === 'min' ? { min: v } : { max: v });
            };
            const ctrl = createRangeControl({
                min: sliderMin, max: sliderMax, step,
                value: key === 'min' ? paramsProxy.colormapMin : paramsProxy.colormapMax,
                format: fmt,
                onInput: (v) => send(v as number),
            });
            cell.appendChild(ctrl.el);
            return ctrl;
        };
        rangeCtrls = { min: mkRow('min', 'Min'), max: mkRow('max', 'Max') };
    };

    /** Lightweight sync: updates widget VALUES only (no extent recompute,
     *  no swatch repaint unless the cmap actually changed). Safe to call
     *  on every params write. */
    const syncValues = (): void => {
        if (!els) return;
        els.transparentBox.checked = !!paramsProxy.transparentBounds;
        els.logBox.checked = !!paramsProxy.colormapLog;
        const unitsMode = paramsProxy.colorbarUnits;
        for (const opt of els.unitsSeg.querySelectorAll<HTMLButtonElement>('.cmap-seg-opt')) {
            const active = opt.dataset.mode === unitsMode;
            opt.classList.toggle('is-active', active);
            opt.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
        const cmap = paramsProxy.colormap;
        if (els.swatchLabel.textContent !== cmap) {
            els.swatchLabel.textContent = cmap;
            paintSwatch(els.swatch, viewer, cmap);
        }
        rangeCtrls.min?.set(paramsProxy.colormapMin);
        rangeCtrls.max?.set(paramsProxy.colormapMax);
    };

    const closePicker = (): void => {
        if (picker) { picker.remove(); picker = null; }
    };

    const openPicker = (): void => {
        if (!els) return;
        closePicker();
        const swatchRow = (els.root as HTMLDivElement & { __swatchRow: HTMLButtonElement }).__swatchRow;
        picker = buildCmapPicker(viewer, (name) => {
            store.dispatch(INTENTS.SET_COLORMAP, { name });
            closePicker();
            // refreshFromParams runs via the params subscription below.
        });
        positionPicker(picker, swatchRow);
    };

    const close = (): void => {
        isOpen = false;
        closePicker();
        disposePane();
        if (els) els.root.classList.remove('open');
        btn.classList.remove('active');
    };

    const ensureBuilt = (): void => {
        if (els) return;
        els = buildPopover(viewer);

        // Wire interactions.
        const swatchRow = (els.root as HTMLDivElement & { __swatchRow: HTMLButtonElement }).__swatchRow;
        swatchRow.addEventListener('click', (e) => {
            e.stopPropagation();
            if (picker) closePicker(); else openPicker();
        });

        els.transparentBox.addEventListener('change', () => {
            store.dispatch(INTENTS.SET_TRANSPARENT_BOUNDS, { value: !!els!.transparentBox.checked });
        });
        els.logBox.addEventListener('change', () => {
            store.dispatch(INTENTS.SET_COLORMAP_LOG, { value: !!els!.logBox.checked });
        });
        els.unitsSeg.addEventListener('click', (e) => {
            const opt = (e.target as HTMLElement).closest<HTMLButtonElement>('.cmap-seg-opt');
            const mode = opt?.dataset.mode;
            if (mode === 'native' || mode === 'code') {
                store.dispatch(INTENTS.SET_COLORBAR_UNITS, { mode });
            }
        });
    };

    const open = (): void => {
        ensureBuilt();
        if (!els) return;
        // Rebuild the sliders each open so newly-loaded data gets fresh extents
        // (the RangeControl locks min/max at construction).
        buildSliders();
        syncValues();
        els.root.classList.add('open');
        // Position after `display` switch flips so getBoundingClientRect is
        // accurate; the 'open' class controls display. Anchor to the gear
        // button (not the colorbar pane) so the popover pops up next to
        // the click; `data-edge` on the pane decides which side it extends.
        positionPopover(els.root, btn, colorbar.dataset.edge ?? 'right');
        btn.classList.add('active');
        isOpen = true;
    };

    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isOpen) close(); else open();
    });

    // Close on outside click or Escape.
    const onDocPointer = (e: Event): void => {
        if (!isOpen) return;
        const t = e.target as Node | null;
        if (els && t && (els.root.contains(t) || btn.contains(t))) return;
        if (picker && t && picker.contains(t)) return;
        close();
    };
    const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape' && isOpen) close();
    };
    document.addEventListener('pointerdown', onDocPointer, true);
    document.addEventListener('keydown', onKey);

    // Reposition on edge change + window resize while open.
    const reposition = (): void => {
        if (!isOpen || !els) return;
        positionPopover(els.root, btn, colorbar.dataset.edge ?? 'right');
        if (picker) {
            const swatchRow = (els.root as HTMLDivElement & { __swatchRow: HTMLButtonElement }).__swatchRow;
            positionPicker(picker, swatchRow);
        }
    };
    colorbar.addEventListener('paneedgechange', reposition);
    window.addEventListener('resize', reposition);
    // Drag-finished reposition (mouseup anywhere may follow a colorbar drag).
    const onPointerUp = (): void => { if (isOpen) reposition(); };
    document.addEventListener('pointerup', onPointerUp);

    // Keep popover in sync when other UIs (left-panel pane folder,
    // dataset switch) change params. Lightweight by design — only updates
    // widget values. If the field identity changes while open, rebuild the
    // sliders so their locked-at-construction extents catch up.
    let lastFieldKey = fieldKey();
    const offSub = store.subscribe('params', () => {
        if (!isOpen) return;
        const key = fieldKey();
        if (key !== lastFieldKey) {
            lastFieldKey = key;
            cachedExtents = null;
            buildSliders();
        }
        syncValues();
    });

    return () => {
        document.removeEventListener('pointerdown', onDocPointer, true);
        document.removeEventListener('keydown', onKey);
        document.removeEventListener('pointerup', onPointerUp);
        colorbar.removeEventListener('paneedgechange', reposition);
        window.removeEventListener('resize', reposition);
        offSub();
        closePicker();
        disposePane();
        if (els) els.root.remove();
    };
}
